import React, { useState, useEffect, useCallback } from 'react'
import { Container, Title, Button, Loader } from "@bsf/force-ui";
import apiFetch from '@wordpress/api-fetch';
import { __ } from '@wordpress/i18n';
import toast, { Toaster } from 'react-hot-toast';
import { FileText, LoaderCircle, RefreshCcw } from 'lucide-react';
import debounce from 'lodash.debounce';

const Integrations = () => {

    const [settings, setSettings] = useState({
        google_api: '', // Match the key with the backend
        developer_mode: false,
        language: '',
        google_places_api: '',
        yelp_api: '',
        recaptcha_v3_key: '',
        recaptcha_v3_secretkey: '',
        recaptcha_v3_score: '0.5',
        google_client_id: '',
        facebook_app_id: '',
        facebook_app_secret: '',
        uael_share_button: '',
        uael_maxmind_geolocation_license_key: '',
        uael_maxmind_geolocation_db_path: '',
        uael_twitter_feed_consumer_key: '',
        uael_twitter_feed_consumer_secret: '',
        instagram_app_id: '',
        instagram_app_secret: '',
        instagram_app_token: '',
        cloudflare_turnstile_site_key: '',
        cloudflare_turnstile_secret_key: '',
    });

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [tokenRefresh, setTokenRefresh] = useState(false);
    const [maxmindStatus, setMaxmindStatus] = useState('success');

    const languagesData = typeof window.uaeLanguagesData === 'object' && window.uaeLanguagesData !== null
        ? Object.entries(window.uaeLanguagesData)
        : []; // Convert object to array of [key, value] pairs

    useEffect(() => {
        const fetchSettings = () => {
            setLoading(true);
            setError(null);
            apiFetch({
                path: '/uael/v1/settings',
                headers: {
                    'Content-Type': 'application/json',
                    'X-WP-Nonce': uaelSettingsData.uael_nonce_action, // Use the correct nonce
                },
            })
                .then((data) => {
                    setSettings(data); // Set the fetched settings
                    setLoading(false); // Stop loading
                })
                .catch((err) => {
                    setError(err.message); // Set error message
                    setLoading(false); // Stop loading
                });

        };

        fetchSettings();
    }, []);

    const validateApi = (apiKey, source) => {

        if ('' === apiKey || '' === source) {
            return;
        }
        const api_type = 'google' === source ? 'Google Places API Key' : ('facebook' === source ? 'Facebook Access Token' : 'Yelp API Key');

        apiFetch({
            path: '/uael/v1/validateApiKey',
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-WP-Nonce': uaelSettingsData.uael_nonce_action, // Use the correct nonce
            },
            body: JSON.stringify({ apiKey, source }),
        }).then((data) => {
            if (data.success) {
                toast.success(`${api_type} authenticated successfully!`);
            } else {
                toast.error(`Entered ${api_type} is invalid`);
            }
        }).catch((error) => {
            toast.error(error.message);
        });
    };

    const debouncedValidateApi = useCallback(debounce(validateApi, 500), []);

    const handleChange = (name, value) => {

        setSettings(prevSettings => ({
            ...prevSettings,
            [name]: value
        }));

        if ('google_places_api' === name) {
            debouncedValidateApi(value, 'google');
        }
        if ('yelp_api' === name) {
            debouncedValidateApi(value, 'yelp');
        }

        if ('uael_share_button' === name) {
            debouncedValidateApi(value, 'facebook');
        }
    };

    const generateInstagramAccessToken = () => {
        const refreshTokenURL = 'https://graph.instagram.com/refresh_access_token';

        if (settings.instagram_app_token) {
            setTokenRefresh(true);

            fetch(refreshTokenURL + `?grant_type=ig_refresh_token&access_token=${settings.instagram_app_token}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            })
                .then((response) => response.json())
                .then((data) => {
                    setTokenRefresh(false);

                    if (data.access_token) {
                        setSettings((prevSettings) => ({
                            ...prevSettings,
                            instagram_app_token: data.access_token, // Update with the new token
                        }));
                        toast.success("Access token refreshed successfully");
                    } else if (data.error) {
                        toast.error(data.error.message || "An error occurred while refreshing the token.");
                    }
                })
                .catch((error) => {
                    setTokenRefresh(false);
                    toast.error("Failed to refresh the access token. Please try again.");
                });
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setError(null);
        setIsSaving(true);

        apiFetch({
            path: '/uael/v1/settings',
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-WP-Nonce': uaelSettingsData.uael_nonce_action,
            },
            data: settings,
        })
            .then((data) => {
                if (data.maxmind_status === 'error') {
                    setMaxmindStatus('error');
                } else {
                    setMaxmindStatus('success');
                }
                setSuccessMessage('Settings saved successfully!'); // Set success message
                toast.success('Settings saved successfully!'); // Show toast notification
            })
            .catch((err) => {
                toast.error('Settings cannot be saved!'); // Show toast notification
            })
            .finally(() => {
                setIsSaving(false);
            });
    };

    if (loading) {
        return (
            <div className='h-screen w-screen flex items-center justify-center' style={{ position: 'relative' }}>
                <Loader icon={null} size="lg" className="bg-transparent relative uae-loader-icon" />
            </div>
        );
    }

    if (error) {
        return <div>Error: {error}</div>;
    }

    return (
        <form
            onSubmit={handleSubmit}
        >
            <div
                className='rounded-lg uael-settings-page'
            >
                <div className='flex flex-row items-center justify-between' style={{ paddingBottom: '24px' }} >
                    <Title
                        description=""
                        icon={null}
                        iconPosition="right"
                        size="sm"
                        tag="h2"
                        title={__('Integrations', 'uael')}
                    />

                    <div className='flex items-center justify-between [&_h2]:text-[#6005ff]' style={{ gap: '22px' }}>
                        {/* <div className='flex items-center gap-1 cursor-pointer' style={{ position: 'relative' }}>
                            <FileText style={{ color: '#6005ff', size: '20px' }} />
                            <a href="https://ultimateelementor.com/docs/how-to-white-label-uael/" target="_blank" className='text-[#6005ff] text-sm font-medium no-underline' style={{ color: '#6005ff', boxShadow: 'none' }}>{__('Read Documentation', 'uael')}</a>
                        </div> */}
                        <Button
                            type="submit"
                            style={{ backgroundColor: '#6005ff', position: 'relative' }}
                            className="flex items-center justify-center"
                            icon={isSaving ? <LoaderCircle className="animate-spin" /> : null}
                            iconPosition="left"
                            disabled={isSaving}
                        >
                            {isSaving ? __('Saving...', 'uael') : __('Save', 'uael')}
                        </Button>
                    </div>
                </div>

                <div className="w-full flex flex-col gap-8" style={{ marginTop: "24px" }}>
                        <Container
                            align="stretch"
                            className="flex flex-column bg-background-primary p-6 rounded-lg"
                            containerType="flex"
                            direction="column"
                        >
                            <Title
                                icon={null}
                                iconPosition="right"
                                size="sm"
                                tag="h2"
                                title={__('Google Map', 'uael')}
                                description=""
                                className=""
                            />
                            <Container.Item className="flex flex-col w-full space-y-1 space-x-2">
                                <Title
                                    icon={null}
                                    iconPosition="right"
                                    size="xs"
                                    tag="h2"
                                    title={__('API Key', 'uael')}
                                    description={
                                        !uaelSettingsData.hide_settings ? (
                                            <>
                                                {__('Note: This setting is required if you wish to use Google Map in your website. ', 'uael')}
                                                <a
                                                    href={`${uaelSettingsData.uael_domain}docs/create-google-map-api-key/?utm_source=uael-pro-dashboard&utm_medium=uael-menu-page&utm_campaign=uael-pro-plugin`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="uael-link-color no-underline"
                                                >
                                                    {__('Need Help?', 'uael')}
                                                </a>.
                                            </>
                                        ) : ''
                                    }
                                    className="uae-subtitle"
                                />
                                <input
                                    type="text"
                                    name="google_api" // Match the key with the backend
                                    value={settings.google_api}
                                    onChange={(e) => handleChange(e.target.name, e.target.value)}
                                    placeholder={__('Paste your API Key here', 'uael')}
                                    className='w-full border border-subtle'
                                    style={{
                                        height: '48px',
                                        borderColor: '#e0e0e0', // Default border color
                                        outline: 'none',       // Removes the default outline
                                        boxShadow: 'none',     // Removes the default box shadow
                                        marginTop: '16px'
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = '#6005FF'} // Apply focus color
                                    onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}  // Revert to default color
                                />
                            </Container.Item>

                            <div className='border border-subtle' style={{ height: '2px', backgroundColor: '#E5E7EB', marginTop: '16px' }} />

                            <Container.Item className="flex flex-col w-full space-y-1 space-x-2">
                                <Title
                                    icon={null}
                                    iconPosition="right"
                                    size="xs"
                                    tag="h2"
                                    title={__('Localization Language', 'uael')}
                                    description={
                                        !uaelSettingsData.hide_settings ? (
                                            <>
                                                {__('Note: This setting sets localization language to google map. The language affects the names of controls, copyright notices, driving directions, and control labels. ', 'uael')}
                                                <a
                                                    href={`${uaelSettingsData.uael_domain}docs/how-to-display-uaels-google-maps-widget-in-your-local-language/?utm_source=uael-pro-dashboard&utm_medium=uael-menu-page&utm_campaign=uael-pro-plugin`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="uael-link-color no-underline"
                                                >
                                                    {__('Need Help?', 'uael')}
                                                </a>.
                                            </>
                                        ) : ''
                                    }
                                    className="uae-subtitle"
                                />
                                <select
                                    name="language"
                                    value={settings.language}
                                    onChange={(e) => handleChange(e.target.name, e.target.value)}
                                    className="border border-subtle rounded p-2 w-full"
                                    style={{
                                        height: '48px',
                                        borderColor: '#e0e0e0', // Default border color
                                        outline: 'none',       // Removes the default outline
                                        boxShadow: 'none',     // Removes the default box shadow
                                        marginTop: '16px'
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = '#6005FF'} // Apply focus color
                                    onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}  // Revert to default color

                                >
                                    <option value="">Default</option>
                                    {languagesData.map(([code, name]) => (
                                        <option key={code} value={code}>
                                            {name}
                                        </option>
                                    ))}
                                </select>
                            </Container.Item>
                        </Container>

                        <Container
                            align="stretch"
                            className="flex flex-column bg-background-primary p-6 rounded-lg"
                            containerType="flex"
                            direction="column"
                        >
                            <Title
                                icon={null}
                                iconPosition="right"
                                size="sm"
                                tag="h2"
                                title={__('Business Reviews', 'uael')}
                                description=""
                                className=""
                            />

                            <Container.Item className="flex flex-col w-full space-y-1 space-x-2">
                                <Title
                                    icon={null}
                                    iconPosition="right"
                                    size="xs"
                                    tag="h2"
                                    title={__('Google Places API Key', 'uael')}
                                    description={
                                        !uaelSettingsData.hide_settings ? (
                                            <>
                                                {__('Note: This setting is required if you wish to use Google Places Reviews in your website. ', 'uael')}
                                                <a
                                                    href={`${uaelSettingsData.uael_domain}docs/get-google-places-api-key/?utm_source=uael-pro-dashboard&utm_medium=uael-menu-page&utm_campaign=uael-pro-plugin`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="uael-link-color no-underline"
                                                >
                                                    {__('Need Help?', 'uael')}
                                                </a>
                                            </>
                                        ) : ''
                                    }
                                    className="uae-subtitle"
                                />
                                <input
                                    type="text"
                                    name="google_places_api" // Match the key with the backend
                                    value={settings.google_places_api}
                                    className='w-full border border-subtle'
                                    onChange={(e) => handleChange(e.target.name, e.target.value)}
                                    placeholder={__('Enter Google Places API Key', 'uael')}
                                    style={{
                                        height: '48px',
                                        borderColor: '#e0e0e0', // Default border color
                                        outline: 'none',       // Removes the default outline
                                        boxShadow: 'none',     // Removes the default box shadow
                                        marginTop: '16px'
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = '#6005FF'} // Apply focus color
                                    onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}  // Revert to default color
                                />
                                <p className='text-sm font-medium mt-2'>
                                    {!uaelSettingsData.hide_settings && (
                                        <>
                                            {__('Google now requires an active billing account associated with your API Key. To enable billing click ', 'uael')}
                                            <a
                                                href={'https://console.cloud.google.com/projectselector2/billing/enable'}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="uael-link-color no-underline"
                                            >
                                                {__('here!', 'uael')}
                                            </a>
                                        </>
                                    )}
                                </p>
                            </Container.Item>

                            <div className='border border-subtle' style={{ height: '2px', backgroundColor: '#E5E7EB', marginTop: '16px' }} />

                            <Container.Item className="flex flex-col w-full space-y-1 space-x-2">
                                <Title
                                    icon={null}
                                    iconPosition="right"
                                    size="xs"
                                    tag="h2"
                                    title={__('Yelp API Key', 'uael')}
                                    description={
                                        !uaelSettingsData.hide_settings ? (
                                            <>
                                                {__('Note: This setting is required if you wish to use Yelp Reviews in your website. ', 'uael')}
                                                <a
                                                    href={`${uaelSettingsData.uael_domain}docs/get-yelp-api-key/?utm_source=uael-pro-dashboard&utm_medium=uael-menu-page&utm_campaign=uael-pro-plugin`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="uael-link-color no-underline"
                                                >
                                                    {__('Need Help?', 'uael')}
                                                </a>
                                            </>
                                        ) : ''
                                    }
                                    className="uae-subtitle"
                                />
                                <input
                                    type="text"
                                    name="yelp_api" // Match the key with the backend
                                    value={settings.yelp_api}
                                    className='w-full border border-subtle px-2'
                                    onChange={(e) => handleChange(e.target.name, e.target.value)}
                                    placeholder={__('Enter Yelp API Key', 'uael')}
                                    style={{
                                        height: '48px',
                                        borderColor: '#e0e0e0', // Default border color
                                        outline: 'none',       // Removes the default outline
                                        boxShadow: 'none',     // Removes the default box shadow
                                        marginTop: '16px'
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = '#6005FF'} // Apply focus color
                                    onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}  // Revert to default color
                                />
                            </Container.Item>
                        </Container>

                        <Container
                            align="stretch"
                            className="flex flex-column bg-background-primary p-6 rounded-lg"
                            containerType="flex"
                            direction="column"
                        >
                            <Title
                                icon={null}
                                iconPosition="right"
                                size="sm"
                                tag="h2"
                                title={__('Setup reCAPTCHA v3', 'uael')}
                                description={
                                    !uaelSettingsData.hide_settings ? (
                                        <>
                                            {__('Note: ', 'uael')}
                                            <a
                                                href={'https://www.google.com/recaptcha/about/'}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="uael-link-color no-underline"
                                            >
                                                {__('reCAPTCHA v3', 'uael')}
                                            </a>
                                            {__(' is a free service by Google that protects your website from spam and abuse. It does this while letting your valid users pass through with ease. ', 'uael')}
                                            <a
                                                href={'https://ultimateelementor.com/docs/user-registration-form-with-recaptcha/?utm_source=uael-pro-dashboard&utm_medium=uael-menu-page&utm_campaign=uael-pro-plugin'}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="uael-link-color no-underline"
                                            >
                                                {__('Need Help?', 'uael')}
                                            </a>
                                        </>
                                    ) : ''
                                }
                            />

                            <Container.Item className="flex flex-col space-y-1 space-x-2">
                                <Title
                                    icon={null}
                                    iconPosition="right"
                                    size="xs"
                                    tag="h2"
                                    title={__('Site key', 'uael')}
                                    description=""
                                    className="uae-subtitle"
                                />
                                {/* <p className='font-normal text-field-label text-base'>{__('Site key', 'uael')}</p> */}
                                <input
                                    type="text"
                                    name="recaptcha_v3_key" // Match the key with the backend
                                    value={settings.recaptcha_v3_key}
                                    className='w-full border border-subtle px-2'
                                    onChange={(e) => handleChange(e.target.name, e.target.value)}
                                    placeholder={__('Enter reCAPTCHA v3 Key', 'uael')}
                                    style={{
                                        height: '48px',
                                        borderColor: '#e0e0e0', // Default border color
                                        outline: 'none',       // Removes the default outline
                                        boxShadow: 'none',     // Removes the default box shadow
                                        // marginTop: '16px'
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = '#6005FF'} // Apply focus color
                                    onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}  // Revert to default color
                                />
                            </Container.Item>

                            <div className="flex flex-row items-center mt-1 w-full gap-3">
                                <Container.Item className="flex flex-col space-y-1 basis-1/2 flex-1">
                                    <Title
                                        icon={null}
                                        iconPosition="right"
                                        size="xs"
                                        tag="h2"
                                        title={__('Secret key', 'uael')}
                                        description=""
                                        className="uae-subtitle"
                                    />
                                    {/* <p className='font-normal text-field-label text-base'>{__('Secret key', 'uael')}</p> */}
                                    <input
                                        type="text"
                                        name="recaptcha_v3_secretkey" // Match the key with the backend
                                        value={settings.recaptcha_v3_secretkey}
                                        onChange={(e) => handleChange(e.target.name, e.target.value)}
                                        className='w-full border border-subtle px-2'
                                        placeholder={__('Enter reCAPTCHA v3 Secret Key', 'uael')}
                                        style={{
                                            height: '48px',
                                            borderColor: '#e0e0e0', // Default border color
                                            outline: 'none',       // Removes the default outline
                                            boxShadow: 'none',     // Removes the default box shadow
                                            // marginTop: '16px'
                                        }}
                                        onFocus={(e) => e.target.style.borderColor = '#6005FF'} // Apply focus color
                                        onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}  // Revert to default color
                                    />
                                </Container.Item>

                                <Container.Item className="flex flex-col space-y-1 basis-1/2 flex-1">
                                    <Title
                                        icon={null}
                                        iconPosition="right"
                                        size="xs"
                                        tag="h2"
                                        title={__('Score Threshold', 'uael')}
                                        description=""
                                        className="uae-subtitle"
                                    />
                                    {/* <p className='font-normal text-field-label text-base'>{__('Score Threshold', 'uael')}</p> */}
                                    <input
                                        type="text"
                                        name="recaptcha_v3_score" // Match the key with the backend
                                        value={settings.recaptcha_v3_score}
                                        onChange={(e) => handleChange(e.target.name, e.target.value)}
                                        className='w-full border border-subtle px-2'
                                        placeholder={__('Enter reCAPTCHA v3 Score', 'uael')}
                                        style={{
                                            height: '48px',
                                            borderColor: '#e0e0e0', // Default border color
                                            outline: 'none',       // Removes the default outline
                                            boxShadow: 'none',     // Removes the default box shadow
                                            // marginTop: '16px'
                                        }}
                                        onFocus={(e) => e.target.style.borderColor = '#6005FF'} // Apply focus color
                                        onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}  // Revert to default color
                                    />
                                </Container.Item>
                            </div>
                        </Container>

                        <Container
                            align="stretch"
                            className="flex flex-column bg-background-primary p-6 rounded-lg"
                            containerType="flex"
                            direction="column"
                        >
                            <Title
                                icon={null}
                                iconPosition="right"
                                size="sm"
                                tag="h2"
                                title={__('Setup Cloudflare Turnstile', 'uael')}
                                description={
                                    !uaelSettingsData.hide_settings ? (
                                        <>
                                            {__('Note: ', 'uael')}
                                            <a
                                                href={'https://www.cloudflare.com/products/turnstile/'}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="uael-link-color no-underline"
                                            >
                                                {__('Cloudflare Turnstile', 'uael')}
                                            </a>
                                            {__(' is a privacy-first alternative to CAPTCHA that protects your website from spam and abuse. ', 'uael')}
                                            <a
                                                href={'https://ultimateelementor.com/docs/how-to-setup-cloudflare-turnstile/?utm_source=uael-pro-dashboard&utm_medium=uael-menu-page&utm_campaign=uael-pro-plugin'}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="uael-link-color no-underline"
                                            >
                                                {__('Need Help?', 'uael')}
                                            </a>
                                        </>
                                    ) : ''
                                }
                            />

                            <Container.Item className="flex flex-col space-y-1 space-x-2">
                                <Title
                                    icon={null}
                                    iconPosition="right"
                                    size="xs"
                                    tag="h2"
                                    title={__('Site key', 'uael')}
                                    description=""
                                    className="uae-subtitle"
                                />
                                <input
                                    type="text"
                                    name="cloudflare_turnstile_site_key"
                                    value={settings.cloudflare_turnstile_site_key}
                                    className='w-full border border-subtle px-2'
                                    onChange={(e) => handleChange(e.target.name, e.target.value)}
                                    placeholder={__('Enter Cloudflare Turnstile Site Key', 'uael')}
                                    style={{
                                        height: '48px',
                                        borderColor: '#e0e0e0', // Default border color
                                        outline: 'none',       // Removes the default outline
                                        boxShadow: 'none',     // Removes the default box shadow
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = '#6005FF'} // Apply focus color
                                    onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}  // Revert to default color
                                />
                            </Container.Item>

                            <Container.Item className="flex flex-col space-y-1 space-x-2 mt-1">
                                <Title
                                    icon={null}
                                    iconPosition="right"
                                    size="xs"
                                    tag="h2"
                                    title={__('Secret key', 'uael')}
                                    description=""
                                    className="uae-subtitle"
                                />
                                <input
                                    type="password"
                                    name="cloudflare_turnstile_secret_key"
                                    value={settings.cloudflare_turnstile_secret_key}
                                    onChange={(e) => handleChange(e.target.name, e.target.value)}
                                    className='w-full border border-subtle px-2'
                                    placeholder={__('Enter Cloudflare Turnstile Secret Key', 'uael')}
                                    style={{
                                        height: '48px',
                                        borderColor: '#e0e0e0', // Default border color
                                        outline: 'none',       // Removes the default outline
                                        boxShadow: 'none',     // Removes the default box shadow
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = '#6005FF'} // Apply focus color
                                    onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}  // Revert to default color
                                />
                            </Container.Item>
                        </Container>

                        <Container
                            align="stretch"
                            className="flex flex-column bg-background-primary p-6 rounded-lg"
                            containerType="flex"
                            direction="column"
                        >
                            <Title
                                icon={null}
                                iconPosition="right"
                                size="sm"
                                tag="h2"
                                title={__('Login Form', 'uael')}
                                description=""
                                className=""
                            />

                            <Container.Item className="flex flex-col w-full space-y-1 space-x-2">
                                <Title
                                    icon={null}
                                    iconPosition="right"
                                    size="xs"
                                    tag="h2"
                                    title={__('Google Client ID', 'uael')}
                                    description={
                                        !uaelSettingsData.hide_settings ? (
                                            <>
                                                {__('Note: This setting is required if you wish to use Login with Google in your website. ', 'uael')}
                                                <a
                                                    href={`${uaelSettingsData.uael_domain}docs/create-google-client-id-for-login-form-widget/?utm_source=uael-pro-dashboard&utm_medium=uael-menu-page&utm_campaign=uael-pro-plugin`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="uael-link-color no-underline"
                                                >
                                                    {__('Need Help?', 'uael')}
                                                </a>
                                            </>
                                        ) : ''
                                    }
                                    className="uae-subtitle"
                                />
                                <input
                                    type="text"
                                    name="google_client_id"
                                    value={settings.google_client_id}
                                    className="w-full border border-subtle rounded p-2"
                                    onChange={(e) => handleChange(e.target.name, e.target.value)}
                                    placeholder={__('Enter Google Client ID', 'uael')}
                                    style={{
                                        height: '48px',
                                        borderColor: '#e0e0e0', // Default border color
                                        outline: 'none',       // Removes the default outline
                                        boxShadow: 'none',     // Removes the default box shadow
                                        marginTop: '16px'
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = '#6005FF'} // Apply focus color
                                    onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}  // Revert to default color
                                />
                            </Container.Item>

                            <div className='border border-subtle' style={{ height: '2px', backgroundColor: '#E5E7EB', marginTop: '16px' }} />

                            <Title
                                icon={null}
                                iconPosition="right"
                                size="xs"
                                tag="h2"
                                title={__('Facebook App Details', 'uael')}
                                description={
                                    !uaelSettingsData.hide_settings ? (
                                        <>
                                            {__('Note: This setting is required if you wish to use Login with Google in your website. ', 'uael')}
                                            <a
                                                href={`${uaelSettingsData.uael_domain}docs/create-facebook-app-id-for-login-form-widget/?utm_source=uael-pro-dashboard&utm_medium=uael-menu-page&utm_campaign=uael-pro-plugin`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="uael-link-color no-underline"
                                            >
                                                {__('Need Help?', 'uael')}
                                            </a>
                                        </>
                                    ) : ''
                                }
                                className=""
                            />

                            <div className="flex flex-row items-center mt-1 w-full gap-3">
                                <Container.Item className="flex flex-col space-y-1 basis-1/2 flex-1">
                                    <Title
                                        icon={null}
                                        iconPosition="right"
                                        size="xs"
                                        tag="h2"
                                        title={__('App ID', 'uael')}
                                        description=""
                                        className="uae-subtitle"
                                    />
                                    {/* <p className='font-normal text-field-label text-base'>{__('App ID', 'uael')}</p> */}
                                    <input
                                        type="text"
                                        name="facebook_app_id" // Match the key with the backend
                                        value={settings.facebook_app_id}
                                        onChange={(e) => handleChange(e.target.name, e.target.value)}
                                        placeholder={__('Enter Facebook App ID', 'uael')}
                                        style={{
                                            height: '48px',
                                            borderColor: '#e0e0e0', // Default border color
                                            outline: 'none',       // Removes the default outline
                                            boxShadow: 'none',     // Removes the default box shadow
                                            // marginTop: '16px'
                                        }}
                                        onFocus={(e) => e.target.style.borderColor = '#6005FF'} // Apply focus color
                                        onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}  // Revert to default color
                                        className='w-full border border-subtle px-2'
                                    />
                                </Container.Item>

                                <Container.Item className="flex flex-col space-y-1 basis-1/2 flex-1">
                                    <Title
                                        icon={null}
                                        iconPosition="right"
                                        size="xs"
                                        tag="h2"
                                        title={__('App Secret', 'uael')}
                                        description=""
                                        className="uae-subtitle"
                                    />
                                    {/* <p className='font-normal text-field-label text-base'>{__('App Secret', 'uael')}</p> */}
                                    <input
                                        type="text"
                                        name="facebook_app_secret" // Match the key with the backend
                                        value={settings.facebook_app_secret}
                                        onChange={(e) => handleChange(e.target.name, e.target.value)}
                                        placeholder={__('Enter Facebook App Secret', 'uael')}
                                        style={{
                                            height: '48px',
                                            borderColor: '#e0e0e0', // Default border color
                                            outline: 'none',       // Removes the default outline
                                            boxShadow: 'none',     // Removes the default box shadow
                                            // marginTop: '16px'
                                        }}
                                        onFocus={(e) => e.target.style.borderColor = '#6005FF'} // Apply focus color
                                        onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}  // Revert to default color
                                        className='w-full border border-subtle px-2'
                                    />
                                </Container.Item>
                            </div>
                        </Container>

                        <Container
                            align="stretch"
                            className="flex flex-column bg-background-primary p-6 rounded-lg"
                            containerType="flex"
                            direction="column"
                        >
                            <Title
                                icon={null}
                                iconPosition="right"
                                size="sm"
                                tag="h2"
                                title={__('Social Share', 'uael')}
                                description=""
                                className=""
                            />

                            <Container.Item className="flex flex-col w-full space-y-1 space-x-2">
                                <Title
                                    icon={null}
                                    iconPosition="right"
                                    size="xs"
                                    tag="h2"
                                    title={__('Facebook Access Token', 'uael')}
                                    description={
                                        !uaelSettingsData.hide_settings ? (
                                            <>
                                                {__('Note: This setting is required if you wish to fetch share count of your post/page from Facebook. ', 'uael')}
                                                <a
                                                    href={`${uaelSettingsData.uael_domain}docs/display-share-count-for-facebook/?utm_source=uael-pro-dashboard&utm_medium=uael-menu-page&utm_campaign=uael-pro-plugin`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="uael-link-color no-underline"
                                                >
                                                    {__('Need Help?', 'uael')}
                                                </a>
                                            </>
                                        ) : ''
                                    }
                                    className="uae-subtitle"
                                />
                                <input
                                    type="text"
                                    name="uael_share_button" // Match the key with the backend
                                    value={settings.uael_share_button}
                                    className="w-full border border-subtle rounded p-2"
                                    onChange={(e) => handleChange(e.target.name, e.target.value)}
                                    placeholder=""
                                    style={{
                                        height: '48px',
                                        borderColor: '#e0e0e0', // Default border color
                                        outline: 'none',       // Removes the default outline
                                        boxShadow: 'none',     // Removes the default box shadow
                                        marginTop: '16px'
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = '#6005FF'} // Apply focus color
                                    onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}  // Revert to default color
                                />
                            </Container.Item>
                        </Container>

                        <Container
                            align="stretch"
                            className="flex flex-column bg-background-primary p-6 rounded-lg"
                            containerType="flex"
                            direction="column"
                        >
                            <Title
                                icon={null}
                                iconPosition="right"
                                size="sm"
                                tag="h2"
                                title={__('MaxMind Geolocation', 'uael')}
                                description={
                                    !uaelSettingsData.hide_settings ? (
                                        <>
                                            {__('Note: An integration for utilizing MaxMind to do Geolocation lookups. Please note that this integration will only do Country lookups. ', 'uael')}
                                            <a
                                                href={`${uaelSettingsData.uael_domain}docs/display-conditions-geolocation/?utm_source=uael-pro-dashboard&utm_medium=uael-menu-page&utm_campaign=uael-pro-plugin`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="uael-link-color no-underline"
                                            >
                                                {__('Need Help?', 'uael')}
                                            </a>
                                        </>
                                    ) : ''
                                }
                                className=""
                            />
                            <div className="flex flex-row items-center mt-1 w-full gap-3">
                                <Container.Item className="flex flex-col space-y-1 basis-1/2 flex-1">
                                    <Title
                                        icon={null}
                                        iconPosition="right"
                                        size="xs"
                                        tag="h2"
                                        title={__('License Key', 'uael')}
                                        description=""
                                        className="uae-subtitle"
                                    />
                                    {/* <p className='font-normal text-field-label text-base'>{__('License Key', 'uael')}</p> */}
                                    <input
                                        type="text"
                                        name="uael_maxmind_geolocation_license_key" // Match the key with the backend
                                        value={settings.uael_maxmind_geolocation_license_key}
                                        onChange={(e) => handleChange(e.target.name, e.target.value)}
                                        placeholder={__('Enter MaxMind License Key', 'uael')}
                                        className='w-full border border-subtle px-2'
                                        style={{
                                            height: '48px',
                                            borderColor: '#e0e0e0', // Default border color
                                            outline: 'none',       // Removes the default outline
                                            boxShadow: 'none',     // Removes the default box shadow
                                            // marginTop: '16px'
                                        }}
                                        onFocus={(e) => e.target.style.borderColor = '#6005FF'} // Apply focus color
                                        onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}  // Revert to default color
                                    />
                                </Container.Item>

                                <Container.Item className="flex flex-col space-y-1 basis-1/2 flex-1">
                                    <Title
                                        icon={null}
                                        iconPosition="right"
                                        size="xs"
                                        tag="h2"
                                        title={__('Database File Path', 'uael')}
                                        description=""
                                        className="uae-subtitle"
                                    />
                                    {/* <p className='font-normal text-field-label text-base'>{__('Database File Path', 'uael')}</p> */}
                                    <input
                                        type="text"
                                        disabled
                                        name="uael_maxmind_geolocation_db_path" // Match the key with the backend
                                        placeholder={uaelSettingsData.maxmind_db_path}
                                        style={{
                                            height: '48px',
                                            borderColor: '#e0e0e0', // Default border color
                                            outline: 'none',       // Removes the default outline
                                            boxShadow: 'none',     // Removes the default box shadow
                                            // marginTop: '16px'
                                        }}
                                        onFocus={(e) => e.target.style.borderColor = '#6005FF'} // Apply focus color
                                        onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}  // Revert to default color
                                        className='w-full border border-subtle px-2'
                                    />
                                </Container.Item>
                            </div>
                            {maxmindStatus === 'error' && (
                                <div className="error-message font-normal my-0 text-sm">
                                    {__('The MaxMind license key is invalid. If you have recently created this key, you may need to wait for it to become active.', 'uael')}
                                </div>
                            )}
                        </Container>

                        <Container
                            align="stretch"
                            className="flex flex-column bg-background-primary p-6 rounded-lg"
                            containerType="flex"
                            direction="column"
                        >
                            <Title
                                icon={null}
                                iconPosition="right"
                                size="sm"
                                tag="h2"
                                title={__('Twitter Feed', 'uael')}
                                description={
                                    !uaelSettingsData.hide_settings ? (
                                        <>
                                            {__('Note: To display your Twitter Feed on your website, you\'ll need to acquire Twitter\'s basic plan and obtain your Twitter Consumer Key and Consumer Secret Key. For guidance on obtaining these keys, ', 'uael')}
                                            <a
                                                href={`${uaelSettingsData.uael_domain}docs/twitter-feed-widget/?utm_source=uael-pro-dashboard&utm_medium=uael-editor-screen&utm_campaign=uael-pro-plugin`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="uael-link-color no-underline"
                                            >
                                                {__('Need Help?', 'uael')}
                                            </a>
                                        </>
                                    ) : ''
                                }
                                className=""
                            />

                            <div className="flex flex-row items-center mt-1 w-full gap-3">
                                <Container.Item className="flex flex-col space-y-1 basis-1/2 flex-1">
                                    <Title
                                        icon={null}
                                        iconPosition="right"
                                        size="xs"
                                        tag="h2"
                                        title={__('Consumer Key', 'uael')}
                                        className="uae-subtitle"
                                    />
                                    {/* <p className='font-normal text-field-label text-base'>{__('Consumer Key', 'uael')}</p> */}
                                    <input
                                        type="text"
                                        name="uael_twitter_feed_consumer_key" // Match the key with the backend
                                        value={settings.uael_twitter_feed_consumer_key}
                                        onChange={(e) => handleChange(e.target.name, e.target.value)}
                                        placeholder={__('Enter Twitter Consumer Key', 'uael')}
                                        style={{
                                            height: '48px',
                                            borderColor: '#e0e0e0', // Default border color
                                            outline: 'none',       // Removes the default outline
                                            boxShadow: 'none',     // Removes the default box shadow
                                            // marginTop: '16px'
                                        }}
                                        onFocus={(e) => e.target.style.borderColor = '#6005FF'} // Apply focus color
                                        onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}  // Revert to default color
                                        className='w-full border border-subtle px-2'
                                    />
                                </Container.Item>

                                <Container.Item className="flex flex-col space-y-1 basis-1/2 flex-1">
                                    <Title
                                        icon={null}
                                        iconPosition="right"
                                        size="xs"
                                        tag="h2"
                                        title={__('Consumer Secret', 'uael')}
                                        className="uae-subtitle"
                                    />
                                    {/* <p className='font-normal text-field-label text-base'>{__('Consumer Secret', 'uael')}</p> */}
                                    <input
                                        type="text"
                                        name="uael_twitter_feed_consumer_secret" // Match the key with the backend
                                        value={settings.uael_twitter_feed_consumer_secret}
                                        onChange={(e) => handleChange(e.target.name, e.target.value)}
                                        placeholder={__('Enter Twitter Consumer Secret', 'uael')}
                                        style={{
                                            height: '48px',
                                            borderColor: '#e0e0e0', // Default border color
                                            outline: 'none',       // Removes the default outline
                                            boxShadow: 'none',     // Removes the default box shadow
                                            // marginTop: '16px'
                                        }}
                                        onFocus={(e) => e.target.style.borderColor = '#6005FF'} // Apply focus color
                                        onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}  // Revert to default color
                                        className='w-full border border-subtle px-2'
                                    />
                                </Container.Item>
                            </div>

                        </Container>

                        <Container
                            align="stretch"
                            className="flex flex-column bg-background-primary p-6 rounded-lg"
                            containerType="flex"
                            direction="column"
                        >
                            <Title
                                icon={null}
                                iconPosition="right"
                                size="sm"
                                tag="h2"
                                title={__('Instagram Feed - Instagram Product API Details', 'uael')}
                                description={
                                    !uaelSettingsData.hide_settings ? (
                                        <>
                                            {__('Note: This setting is required if you wish to use Instagram Feed in your website. ', 'uael')}
                                            <a
                                                href={`${uaelSettingsData.uael_domain}docs/instagram-feed-widget/?utm_source=uael-pro-dashboard&utm_medium=uael-editor-screen&utm_campaign=uael-pro-plugin`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="uael-link-color no-underline"
                                            >
                                                {__('Need Help?', 'uael')}
                                            </a>
                                        </>
                                    ) : ''
                                }
                                className=""
                            />

                            <div className="flex flex-row items-center mt-1 w-full gap-3">
                                <Container.Item className="flex flex-col space-y-1 basis-1/2 flex-1">
                                    <Title
                                        icon={null}
                                        iconPosition="right"
                                        size="xs"
                                        tag="h2"
                                        title={__('App ID', 'uael')}
                                        description=""
                                        className="uae-subtitle"
                                    />
                                    {/* <p className='font-normal text-field-label text-base'>{__('App ID', 'uael')}</p> */}
                                    <input
                                        type="text"
                                        name="instagram_app_id" // Match the key with the backend
                                        value={settings.instagram_app_id}
                                        onChange={(e) => handleChange(e.target.name, e.target.value)}
                                        placeholder={__('Enter Instagram App ID', 'uael')}
                                        className='w-full border border-subtle px-2'
                                        style={{
                                            height: '48px',
                                            borderColor: '#e0e0e0', // Default border color
                                            outline: 'none',       // Removes the default outline
                                            boxShadow: 'none',     // Removes the default box shadow
                                            // marginTop: '16px'
                                        }}
                                        onFocus={(e) => e.target.style.borderColor = '#6005FF'} // Apply focus color
                                        onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}  // Revert to default color
                                    />
                                </Container.Item>

                                <Container.Item className="flex flex-col space-y-1 basis-1/2 flex-1">
                                    <Title
                                        icon={null}
                                        iconPosition="right"
                                        size="xs"
                                        tag="h2"
                                        title={__('App Secret', 'uael')}
                                        description=""
                                        className="uae-subtitle"
                                    />
                                    {/* <p className='font-normal text-field-label text-base'>{__('App Secret', 'uael')}</p> */}
                                    <input
                                        type="text"
                                        name="instagram_app_secret" // Match the key with the backend
                                        value={settings.instagram_app_secret}
                                        onChange={(e) => handleChange(e.target.name, e.target.value)}
                                        placeholder={__('Enter Instagram App Secret', 'uael')}
                                        className='w-full border border-subtle px-2'
                                        style={{
                                            height: '48px',
                                            borderColor: '#e0e0e0', // Default border color
                                            outline: 'none',       // Removes the default outline
                                            boxShadow: 'none',     // Removes the default box shadow
                                            // marginTop: '16px'
                                        }}
                                        onFocus={(e) => e.target.style.borderColor = '#6005FF'} // Apply focus color
                                        onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}  // Revert to default color
                                    />
                                </Container.Item>
                            </div>

                            <Container.Item className="flex flex-col w-full space-y-1 space-x-2 mt-1">
                                <div className='flex flex-row items-center justify-between pb-1'>
                                    <Title
                                        icon={null}
                                        iconPosition="right"
                                        size="xs"
                                        tag="h2"
                                        title={__('Access Token', 'uael')}
                                        description=""
                                        className="uae-subtitle"
                                    />
                                    {/* <p className='font-normal text-field-label text-base'>{__('Access Token', 'uael')}</p> */}
                                    <Button
                                        icon={tokenRefresh ? <LoaderCircle className="animate-spin" /> : <RefreshCcw style={{ color: "#6005FF" }} />}
                                        className="uael-remove-ring"
                                        variant="link"
                                        iconPosition="left"
                                        onClick={generateInstagramAccessToken}
                                        style={{ width: 'fit-content', outline: 'none', textDecoration: 'none', color: '#6005FF' }}
                                    >
                                        {tokenRefresh ? __('Refreshing token...', 'uael') : __('Refresh Access Token', 'uael')}
                                    </Button>
                                </div>
                                <input
                                    type="text"
                                    name="instagram_app_token" // Match the key with the backend
                                    value={settings.instagram_app_token}
                                    onChange={(e) => handleChange(e.target.name, e.target.value)}
                                    placeholder={__('Enter Instagram Access Token', 'uael')}
                                    className='w-full border border-subtle'
                                    style={{
                                        height: '48px',
                                        borderColor: '#e0e0e0', // Default border color
                                        outline: 'none',       // Removes the default outline
                                        boxShadow: 'none',     // Removes the default box shadow
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = '#6005FF'} // Apply focus color
                                    onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}  // Revert to default color
                                />
                            </Container.Item>
                        </Container>
                        <Button
                            type="submit"
                            style={{ backgroundColor: '#6005ff' }}
                            icon={isSaving ? <LoaderCircle className="animate-spin" /> : null}
                            iconPosition="left"
                            disabled={isSaving}
                            className="w-full mt-4 sticky"
                        >
                            {isSaving ? __('Saving...', 'uael') : __('Save', 'uael')}
                        </Button>

                        <Toaster
                            position="top-right"
                            reverseOrder={false}
                            gutter={8}
                            containerStyle={{
                                top: 20,
                                right: 20,
                                marginTop: '80px',
                            }}
                            toastOptions={{
                                duration: 5000,
                                style: {
                                    background: 'white',
                                },
                                success: {
                                    duration: 3000,
                                    style: {
                                        color: '',
                                    },
                                    iconTheme: {
                                        primary: '#6005ff',
                                        secondary: '#fff',
                                    },
                                },
                            }}
                        />
                </div>
            </div>
        </form>
    );
}

export default Integrations
